import { CalendarDays, Timer } from "lucide-react";
import { ATTENDANCE_STATUS_CONFIG } from "../../../Moduels/attendance/indianCalendarUtils";
import {
  isWeekOffWeekdayName,
  WEEKDAY_NAMES,
} from "../../../Moduels/attendance/attendanceCalendarUtils";

function formatHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

function cellClassName({ statusCode, holiday, isWeekOff, otRow, selected }) {
  const parts = ["ot-cal-cell", "ot-cal-cell--clickable"];
  if (selected) parts.push("ot-cal-cell--selected");
  if (otRow) {
    if (otRow.status === "PENDING") parts.push("ot-cal-cell--pending", "ot-cal-cell--has-ot");
    else if (otRow.status === "APPROVED") parts.push("ot-cal-cell--has-ot");
    else if (otRow.status === "REJECTED" || otRow.status === "CANCELLED") parts.push("ot-cal-cell--blocked");
    else parts.push("ot-cal-cell--has-ot");
  } else if (statusCode === "H" || holiday) {
    parts.push("ot-cal-cell--holiday");
  } else if (statusCode === "WO" || isWeekOff) {
    parts.push("ot-cal-cell--weekoff");
  } else if (statusCode === "P" || statusCode === "HD") {
    parts.push("ot-cal-cell--present");
  } else if (statusCode === "A") {
    parts.push("ot-cal-cell--absent");
  } else if (statusCode === "L") {
    parts.push("ot-cal-cell--leave");
  }
  return parts.join(" ");
}

/**
 * Month grid for OT allocation: attendance status + holidays / week-offs + OT hours.
 */
export default function OtAllocationCalendar({
  monthKey,
  cells = [],
  weekdayHeaders = WEEKDAY_NAMES,
  dayStatusMap = {},
  holidayMap = {},
  weekOffWeekdays = null,
  otByDay = {},
  countMode = false,
  selectedDay = null,
  busy = false,
  onDayClick,
}) {
  return (
    <div className="ot-cal-wrap">
      <div className="ot-cal-grid">
        {weekdayHeaders.map((name) => (
          <div
            key={name}
            className={`ot-cal-head${
              isWeekOffWeekdayName(name, weekOffWeekdays) ? " ot-cal-head--off" : ""
            }`}
          >
            <span className="hidden sm:inline">{name}</span>
            <span className="sm:hidden">{name.slice(0, 3)}</span>
          </div>
        ))}

        {cells.map((cell) => {
          if (cell.empty) {
            return <div key={cell.key} className="ot-cal-cell ot-cal-cell--empty" aria-hidden />;
          }

          const { day, weekdayName } = cell;
          const statusCode = dayStatusMap[day] || dayStatusMap[String(day)] || "";
          const holiday = holidayMap[day];
          const isWeekOff = isWeekOffWeekdayName(weekdayName, weekOffWeekdays);
          const otRow = otByDay[day] || otByDay[String(day)] || null;
          const statusCfg = statusCode ? ATTENDANCE_STATUS_CONFIG[statusCode] : null;
          const hoursLabel = otRow ? formatHours(otRow.ot_hours) : null;
          const countLabel = otRow ? formatHours(otRow.ot_count) : null;
          const qtyLabel = countMode ? (countLabel ? `${countLabel} OT` : null) : hoursLabel ? `${hoursLabel}h` : null;
          const selected = selectedDay != null && Number(selectedDay) === day;
          const iso = `${monthKey}-${String(day).padStart(2, "0")}`;

          let tag = null;
          if (otRow) {
            tag = otRow.status === "PENDING" ? "OT pending" : otRow.status === "APPROVED" ? "OT approved" : `OT · ${otRow.status}`;
          } else if (holiday) {
            tag = holiday.name || "Holiday";
          } else if (statusCode === "WO" || isWeekOff) {
            tag = "Week off";
          } else if (statusCfg) {
            tag = statusCfg.label;
          }

          return (
            <button
              key={cell.key}
              type="button"
              disabled={busy}
              className={cellClassName({ statusCode, holiday, isWeekOff, otRow, selected })}
              onClick={() => onDayClick?.(day)}
              title={
                otRow
                  ? countMode
                    ? `${iso} · ${formatHours(otRow.ot_count) || "?"} OT · ${formatHours(otRow.ot_hours) || "?"}h on record · ${otRow.status}`
                    : `${iso} · ${formatHours(otRow.ot_hours) || "?"}h OT · ${otRow.status}`
                  : holiday
                    ? `${iso} · ${holiday.name}`
                    : statusCfg
                      ? `${iso} · ${statusCfg.label}`
                      : `${iso} · no OT`
              }
            >
              <span className="ot-cal-daynum">{day}</span>
              {statusCfg || holiday || isWeekOff ? (
                <span
                  className={`ot-cal-status ot-cal-status--${(
                    statusCode ||
                    (holiday ? "H" : isWeekOff ? "WO" : "")
                  ).toLowerCase()}`}
                >
                  {statusCfg?.short || (holiday ? "H" : isWeekOff ? "WO" : "")}
                </span>
              ) : null}

              {qtyLabel ? (
                <span className="ot-cal-hours">
                  <Timer size={11} aria-hidden /> {qtyLabel}
                </span>
              ) : (
                <span className="ot-cal-hours ot-cal-hours--empty" />
              )}

              {tag ? (
                <span className={`ot-cal-tag${otRow ? "" : " ot-cal-tag--muted"}`} title={tag}>
                  {tag}
                </span>
              ) : (
                <span className="ot-cal-tag ot-cal-tag--muted">—</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="ot-legend mt-3">
        <span className="ot-legend__item">
          <span className="ot-dot ot-dot--ot" /> OT day
        </span>
        <span className="ot-legend__item">
          <span className="ot-dot ot-dot--pending" /> Pending approval
        </span>
        <span className="ot-legend__item">
          <span className="ot-dot ot-dot--present" /> Present
        </span>
        <span className="ot-legend__item">
          <span className="ot-dot" style={{ background: "#a78bfa" }} /> Holiday
        </span>
        <span className="ot-legend__item">
          <span className="ot-dot" style={{ background: "#818cf8" }} /> Week off
        </span>
        <span className="ot-legend__item">
          <span className="ot-dot ot-dot--absent" /> Absent / blocked
        </span>
      </div>
    </div>
  );
}

export function OtCalendarEmptyState({ message, onAdd, canAdd }) {
  return (
    <div className="ot-empty">
      <CalendarDays size={28} aria-hidden />
      <p>{message}</p>
      {canAdd ? (
        <button type="button" className="ot-add-btn ot-add-btn--sm" onClick={onAdd}>
          + Add OT
        </button>
      ) : null}
    </div>
  );
}
